import { AvailableInstance, IntegrationsMappedOptions } from '@/types/integrations.types';
import Icon from '@ui/icons/icon';
import { ICONS } from '@ui/icons/types';
import InputWithOverlay from '@ui/input-with-overlay/input-with-overlay';
import OptionalComponent from '@ui/optional-component/optional-component';
import { ChangeEvent, useMemo, useState } from 'react';
import QuestionMapping from './question-mapping';
import { v4 as uuid } from 'uuid';
import { selectFeatureFlag } from '@/store/slices/feature-flags/selectors';
import { useAppSelector } from '@/store/hooks';
import { FeatureFlagsEnum } from '@/store/slices/feature-flags/types';
import TooltipPortal from '@ui/tooltip-portal/tooltip-portal';
import Switch from '@ui/switch/switch';
import StaticIntegrationMapping from './static-mapping';
import QueriesMapping from './queries-mapping';
import OptinMapping from './optin-mapping';
import {
  fieldObjectToFieldId,
  fieldsToArray,
  filterOutValidFields,
  getDuplicates,
  TempRegField,
} from '@/utils/integrations';
import useLoadRegistrationQuestions from '@/connection/hooks/use-load-registration-questions';
import { getRegistrationQuestions } from '@/store/slices/channel/selectors';
import { getWorkingProject } from '@/store/slices/project/selectors';

const placeholderField = '-1'; // default field value used as placeholder for new mapping
export interface SelectOption {
  label: string;
  value: string | number;
  code?: string;
}
interface RegistrationIntegrationFormProps {
  instance: AvailableInstance;
  values?: IntegrationsMappedOptions;
  onChange: ({
    registrationMapping,
    staticMapping,
    queryMapping,
    optInMapping,
    actionTitleKeyname,
    confirmationUrlMapping,
    valid,
  }: {
    registrationMapping?: Record<string, string>;
    staticMapping?: Record<string, string>;
    queryMapping?: Record<string, string>;
    optInMapping?: string;
    actionTitleKeyname?: string;
    confirmationUrlMapping?: string;
    valid?: boolean;
    convertNonAsciiCharacters?: boolean;
  }) => void;
  solutionTitle?: string;
  channelIntegration?: boolean;
}

function RegistrationIntegrationForm({
  instance,
  values,
  onChange,
  solutionTitle,
  channelIntegration,
}: RegistrationIntegrationFormProps) {
  useLoadRegistrationQuestions();

  const workingProject = useAppSelector(getWorkingProject);
  const registrationQuestions = useAppSelector(getRegistrationQuestions);
  const canUseConfirmationUrls = useAppSelector(selectFeatureFlag(FeatureFlagsEnum.integrations_confirmation_urls));

  const [defaultsExpanded, setDefaultsExpanded] = useState(false);

  const { registrationQuestionsOptions, registrationQuestionsOptionsMap } = useMemo(() => {
    const options = [];
    const map: Record<number, SelectOption> = {};

    for (const question of registrationQuestions) {
      const option = {
        label: question.name,
        value: question.registration_question,
      };
      options.push(option);
      map[question.registration_question] = option;
    }

    return { registrationQuestionsOptions: options, registrationQuestionsOptionsMap: map };
  }, [registrationQuestions]);

  const [regFieldsTemp, setRegFieldsTemp] = useState(
    values
      ? fieldsToArray(values.registration_mapping, placeholderField, true)
      : fieldsToArray(instance.registration_mapping, placeholderField)
  );

  const [fieldErrors, setFieldErrors] = useState<string[]>([]);

  const optinField = values ? values.opt_in_mapping : instance.opt_in_mapping;
  const [queryFieldsTemp, setQueryFieldsTemp] = useState(
    values ? fieldsToArray(values.query_mapping, '') : fieldsToArray(instance.query_mapping, '')
  );
  const [queryFieldErrors, setQueryFieldErrors] = useState<string[]>([]);
  const [staticMappingValid, setStaticMappingValid] = useState(true);

  const [confirmationUrlMappingValid, setConfirmationUrlMappingValid] = useState(true);

  function formIsValid(ignorableField?: string): boolean {
    const validilityMap: Record<string, boolean> = {
      registrationMapping: fieldErrors.length === 0,
      staticMapping: staticMappingValid,
      queryMapping: queryFieldErrors.length === 0,
      confirmationUrlMapping: confirmationUrlMappingValid,
    };

    if (ignorableField) {
      delete validilityMap[ignorableField];
    }

    const valid = Object.values(validilityMap).every((isValid) => isValid);

    return valid;
  }

  function handleStaticIntegrationMappingChange({
    staticMapping,
    valid,
  }: {
    staticMapping: Record<string, string>;
    valid: boolean;
  }): void {
    onChange({ staticMapping, valid: valid && formIsValid('staticMapping') });
    setStaticMappingValid(valid);
  }

  function handleAddQueryParam(): void {
    const fields = [...queryFieldsTemp, { field: '', value: '', id: uuid() }];
    handleUpdateQueryFields(fields);
  }

  function handleTitleKeynameChange(event: ChangeEvent<HTMLInputElement>): void {
    const value = event.target.value.trim();
    onChange({ actionTitleKeyname: value });
  }

  function handleConfirmationUrlMappingChange(e: ChangeEvent<HTMLInputElement>): void {
    const value = e.target.value.trim();
    const valid = /\s/g.test(value);

    setConfirmationUrlMappingValid(valid);

    onChange({ confirmationUrlMapping: value, valid: valid && formIsValid('confirmationUrlMapping') });
  }

  function handleAddRegField(): void {
    const fields = [...regFieldsTemp, { field: '', value: '', id: uuid() }];
    setRegFieldsTemp(fields);
    handleRegFieldsTempUpdate(fields);
  }

  function handleToggleConvertNonAsciiCharacters(value: string, on: boolean): void {
    if (!channelIntegration) {
      console.warn(`Not yet implemented since it doesn't work on projects`, value, on);
    } else {
      onChange({ convertNonAsciiCharacters: on });
    }
  }

  function handleUpdateRegistrationFields(index: number) {
    return (field: string, value: string) => {
      const registrationMappingArray = [...regFieldsTemp];
      registrationMappingArray[index] = { field: field.toString(), value, id: uuid() };

      setRegFieldsTemp(registrationMappingArray);

      handleRegFieldsTempUpdate(registrationMappingArray);
    };
  }

  function handleRegFieldsTempUpdate(tempRegFields: TempRegField[]) {
    const duplicates = getDuplicates(tempRegFields);

    const empties = tempRegFields.filter(filterOutValidFields).map<string>(fieldObjectToFieldId);

    const placeholderFields = tempRegFields
      .filter((f) => f.field.toString() === placeholderField)
      .map<string>(fieldObjectToFieldId);

    const errors = Array.from(new Set<string>([...duplicates, ...empties, ...placeholderFields]));
    setFieldErrors(errors);
    const valid = errors.length === 0;

    const registrationMapping = Object.fromEntries(
      tempRegFields.map((tempField) => [tempField.field, tempField.value])
    );

    onChange({ registrationMapping, valid: valid && formIsValid('registrationMapping') });

    return errors.length === 0;
  }

  function deleteQuestion(index: number): () => void {
    return () => {
      const fields = regFieldsTemp.filter((item, i) => i !== index);
      setRegFieldsTemp([...fields]);
      handleRegFieldsTempUpdate(fields);
    };
  }

  function handleUpdateQueryParam(index: number): (field: string, value: string) => void {
    return (field: string, value: string) => {
      const id = queryFieldsTemp[index].id;
      const fields = queryFieldsTemp.map((item, i) => (index === i ? { field, value, id } : item));
      handleUpdateQueryFields(fields);
    };
  }

  const handleUpdateQueryFields = (fields: TempRegField[]) => {
    setQueryFieldsTemp(fields);
    const queryMapping = Object.fromEntries(fields.map((tempField) => [tempField.field, tempField.value]));
    const duplicates = getDuplicates(fields);
    const empties = fields.filter(filterOutValidFields).map<string>(fieldObjectToFieldId);
    const errors = Array.from(new Set<string>([...duplicates, ...empties]));
    setQueryFieldErrors(errors);
    onChange({
      queryMapping,
      valid: errors.length === 0 && formIsValid('queryMapping'),
    });
  };

  function deleteQuery(index: number): () => void {
    return () => {
      const fields = queryFieldsTemp.filter((item, i) => i !== index);
      handleUpdateQueryFields([...fields]);
    };
  }

  function handleSetOptIn(value: string): void {
    onChange({ optInMapping: value });
  }

  const eventRegistrationMappings = useMemo(() => {
    const array = [];
    if (instance?.registration_mapping) {
      for (const key in instance?.registration_mapping) {
        const regOption = registrationQuestionsOptions?.find((question) => question.value === Number(key));
        if (regOption) {
          array.push(
            <QuestionMapping
              key={regOption.value + '#' + key}
              channelRegistrationQuestions={registrationQuestionsOptions}
              optionsMap={registrationQuestionsOptionsMap}
              setQuestion={() => null}
              field={{ field: regOption.value, value: instance.registration_mapping[key], id: uuid() }}
              error={false}
              deleteQuestion={() => null}
              disabled
            />
          );
        }
      }
    }
    return array;
  }, [instance.registration_mapping, registrationQuestionsOptions, registrationQuestionsOptionsMap]);

  const eventQueryMappings = useMemo(() => {
    const array = [];
    if (instance?.query_mapping) {
      for (const key in instance?.query_mapping) {
        array.push(
          <QueriesMapping
            key={'#' + key}
            setQuery={() => null}
            field={{ field: key, value: instance.query_mapping[key], id: uuid() }}
            error={false}
            deleteQuery={() => null}
            disabled
          />
        );
      }
    }
    return array;
  }, [instance?.query_mapping]);

  return (
    <>
      <div className="event-identification">
        <div className="grid-row column">
          <label className="section-title registration-questions-label" id="event-identification-section">
            Project Identification
          </label>
        </div>
      </div>
      <div className="registration-field">
        <div className="grid-row">
          <label className="registration-questions-label grid-row-label" id="registration-questions-label">
            Brandlive
          </label>
          <label className="grid-row-label" id="third-integration-label">
            {solutionTitle}
          </label>
        </div>
        <div className="event-identification-fields grid-row">
          <InputWithOverlay
            inputProps={{
              disabled: true,
              placeholder: workingProject?.name ?? 'Project Name',
              style: { fontFamily: 'monospace' },
            }}
          />
          <InputWithOverlay
            inputProps={{
              onChange: handleTitleKeynameChange,
              placeholder: 'Enter project title key mapping here',
              defaultValue: values?.action_title_keyname || instance.action_title_keyname,
              style: { fontFamily: 'monospace' },
            }}
          />
        </div>
      </div>
      <OptionalComponent display={canUseConfirmationUrls}>
        <div className="event-identification">
          <div className="grid-row column">
            <label className="section-title registration-questions-label" id="event-identification-section">
              Registration Confirmation Links
            </label>
            <label>Confirmation links are sent to each registrant with a unique access link</label>
          </div>
        </div>
        <div className="registration-field">
          <div className="event-identification-fields">
            <InputWithOverlay
              inputProps={{
                disabled: true,
                placeholder: 'Confirmation URL',
                style: { fontFamily: 'monospace' },
              }}
            />
            <InputWithOverlay
              inputProps={{
                onBlur: handleConfirmationUrlMappingChange,
                placeholder: 'Enter confirmation url key mapping here',
                defaultValue: values?.confirmation_url_mapping || instance?.confirmation_url_mapping || '',
                style: { fontFamily: 'monospace' },
              }}
              error={confirmationUrlMappingValid}
            />
          </div>
        </div>
      </OptionalComponent>
      <OptionalComponent display={!channelIntegration}>
        <div className="integration-defaults margin-t-24">
          <div className="grid-row">
            <label className="section-title" id="event-identification-section">
              Integration Defaults
            </label>
            <div className="flex jc-flex-end">
              <button className="clear-button no-padding" onClick={() => setDefaultsExpanded(!defaultsExpanded)}>
                <Icon
                  name={defaultsExpanded ? ICONS.KEYBOARD_ARROW_UP : ICONS.KEYBOARD_ARROW_DOWN}
                  size={16}
                  color="var(--gray-100)"
                />
              </button>
            </div>
          </div>
        </div>
      </OptionalComponent>
      <OptionalComponent display={defaultsExpanded}>
        <div className="registration-defaults">
          <div className="registration-field">
            <div className="grid-row">
              <label className="registration-questions-label grid-row-label" id="registration-questions-label">
                Brandlive*
              </label>
              <label className="third-integration-label grid-row-label">{instance?.name}*</label>
            </div>
          </div>
          {eventRegistrationMappings}
          <div className="registration-field">
            <div className="grid-row">
              <label className="registration-questions-label grid-row-label" id="registration-questions-label">
                Query key*
              </label>
              <label className="third-integration-label grid-row-label">Key mapping*</label>
            </div>
          </div>
          {eventQueryMappings}
        </div>
      </OptionalComponent>
      <div className="registration-field">
        <div className="grid-row column">
          <label className="section-title registration-questions-label" id="registration-fields-section">
            Field Mapping
          </label>
        </div>
      </div>
      {/* Registration questions's mapping */}
      <div className="registration-field">
        <div className="grid-row">
          <label className="registration-questions-label grid-row-label" id="registration-questions-label">
            Brandlive*
          </label>
          <label id="third-integration-label" className="grid-row-label">
            {solutionTitle}*
          </label>
        </div>
        {regFieldsTemp.map((question: TempRegField, index: number) => (
          <QuestionMapping
            key={question.field + '#' + (queryFieldsTemp[index]?.id || uuid())}
            channelRegistrationQuestions={registrationQuestionsOptions}
            optionsMap={registrationQuestionsOptionsMap}
            setQuestion={handleUpdateRegistrationFields(index)}
            field={question}
            error={fieldErrors.includes(question.field.toString()) && question.field.toString() !== placeholderField}
            isKeyError={question.field.toString() === placeholderField}
            deleteQuestion={deleteQuestion(index)}
          />
        ))}
      </div>

      <button className="small" onClick={handleAddRegField}>
        <Icon name={ICONS.ADD} color="var(--gray--200)" size={12} />
        <span className="margin-l-4">Add</span>
      </button>
      <div className="registration-field">
        <div className="grid-row column">
          <label className="section-title registration-questions-label" id="registration-fields-section">
            Marketing Email Opt-in Mapping
          </label>
        </div>
      </div>
      <div className="registration-field">
        <div className="grid-row">
          <label className="grid-row-label">{solutionTitle}*</label>
        </div>
        <OptinMapping setOptin={handleSetOptIn} field={optinField} error={false} />
      </div>
      <div className="utm-parameters margin-t-24">
        <div className="grid-row column">
          <label className="section-title registration-questions-label" id="event-identification-section">
            UTM Parameters
          </label>
        </div>
        <label className="accent-text">Track the performance of webpage or campaign</label>
      </div>

      {/* Queries 's mapping */}
      <div className="query-field">
        <div className="grid-row">
          <label className="query-label grid-row-label" id="query-label">
            Query key
          </label>
          <label id="query-value" className="grid-row-label">
            Key mapping
          </label>
        </div>
        {queryFieldsTemp.map((queryString: TempRegField, index: number) => (
          <QueriesMapping
            key={'#' + (queryFieldsTemp[index]?.id || uuid())}
            setQuery={handleUpdateQueryParam(index)}
            field={queryString}
            error={queryFieldErrors.includes(queryString.field.toString())}
            deleteQuery={deleteQuery(index)}
          />
        ))}
      </div>

      <button className="small" onClick={handleAddQueryParam}>
        <Icon name={ICONS.ADD} color="var(--gray--200)" size={12} />
        <span className="margin-l-4">Add</span>
      </button>

      <StaticIntegrationMapping
        integration={instance}
        overrides={values}
        onChange={handleStaticIntegrationMappingChange}
      />

      <OptionalComponent display={channelIntegration}>
        <div className="convert-ascii-toggle flex gap-8 margin-t-16">
          <label className="flex gap-8 ai-center">
            Convert special characters
            <TooltipPortal
              tooltipContent={
                <div>Enabling this will convert any entered non-ASCII characters to ASCII characters.</div>
              }
            >
              <Icon name={ICONS.INFO_OUTLINE} size={14} color="var(--gray-100)" />
            </TooltipPortal>
          </label>
          <div className="flex ai-center">
            <Switch
              on={
                channelIntegration
                  ? values?.convert_non_ascii_characters || false
                  : instance.convert_non_ascii_characters || false
              }
              onClick={handleToggleConvertNonAsciiCharacters}
              value="Convert Non ASCII Characters"
            />
          </div>
        </div>
      </OptionalComponent>
    </>
  );
}

export default RegistrationIntegrationForm;
